import { loggedUser, promiseWrapper } from '@/helpers';
import { getCookie, removeCookie, setCookie } from '@/helpers/cookies';
import { _s } from '@/locale';
import { autocompleteService, invalidateLocationSuggestionsCache } from '@/services';
import { ResolvedLocation } from '@/types/api/services/search';
import Fuse from 'fuse.js';
import { AutoSuggestionSection } from '../shared/types';
import {
  LAST_LOCATION_ANONYMOUS_COOKIE,
  LAST_LOCATION_ANONYMOUS_COOKIE_MAX_AGE,
  LOCAL_STORAGE_KEY_LAST_SEARCHED_LOCATION,
  NO_DEFAULT_LOCATION_COOKIE,
  resolveDefaultLocation,
} from './LocationAutoSuggest.shared';
import {
  AnonymousLocationCookie,
  anonymousLocationCookieSchema,
  LocationSuggestion,
  locationSuggestionSchema,
} from './LocationAutoSuggest.types';

export function setNoDefaultLocation() {
  setCookie(NO_DEFAULT_LOCATION_COOKIE, 'true', { path: '/' });
}

export function clearNoDefaultLocation() {
  removeCookie(NO_DEFAULT_LOCATION_COOKIE, { path: '/' });
}

export function hasNoDefaultLocation(): boolean {
  return Boolean(getCookie(NO_DEFAULT_LOCATION_COOKIE));
}

export async function getSuggestionsFromSearchValue(
  value: string,
  signal?: AbortSignal,
): Promise<{
  hasHistory: boolean;
  suggestions: AutoSuggestionSection<LocationSuggestion>[];
  storedSuggestions: AutoSuggestionSection<LocationSuggestion>[];
}> {
  const { data, error } = await promiseWrapper(autocompleteService.getLocationSuggestions(value, signal));

  if (error) {
    console.error('Error while fetching location suggestions: ', error);
    return { suggestions: [], storedSuggestions: [], hasHistory: false };
  }

  const rawResults = data?.results || [];
  const history = (data?.history || []).filter((item) => item && item.label);
  const popular = (data?.popular || []).filter((item) => item && item.label);

  const suggestions: LocationSuggestion[] = [
    { type: 'geolocation', label: _s('currentLocation') },
    ...rawResults.map((item: LocationSuggestion, i: number) => (i === 0 ? { ...item, highlight: true } : item)),
  ];

  const suggestionsArray = [
    {
      type: 'results',
      title: '',
      suggestions: suggestions,
    },
  ];

  const storedSuggestions = [
    {
      type: 'results',
      title: '',
      suggestions: suggestions,
    },
  ];

  /**
   * Fuzzy search (fuse.js https://www.fusejs.io/api/options.html) for history suggestions based on the search value
   * If the search value is empty, return all history suggestions
   */
  let historySuggestions = [];
  if (!value.trim()) {
    historySuggestions = history;
  } else {
    const results = new Fuse(history, { keys: ['label'], threshold: 0.2, includeScore: true }).search(value);
    historySuggestions = results.sort((a, b) => a.score - b.score).map((result) => result.item);
  }

  const hasHistory = !!historySuggestions.length;

  if (hasHistory) {
    suggestionsArray.push({
      type: 'history',
      title: 'history',
      suggestions: historySuggestions.slice(0, 3),
    });

    storedSuggestions.push({
      type: 'history',
      title: 'history',
      suggestions: historySuggestions,
    });
  }

  suggestionsArray.push({
    type: 'popular',
    title: 'popular',
    suggestions: popular,
  });

  storedSuggestions.push({
    type: 'popular',
    title: 'popular',
    suggestions: popular,
  });

  return { suggestions: suggestionsArray, storedSuggestions, hasHistory };
}

export function setLastLocationSuggestionToLocalStorage(suggestion: LocationSuggestion) {
  const validate = locationSuggestionSchema.safeParse(suggestion);

  if (validate.success === false) {
    setNoDefaultLocation();
    return;
  }

  localStorage.setItem(LOCAL_STORAGE_KEY_LAST_SEARCHED_LOCATION, JSON.stringify(validate.data));
  invalidateLocationSuggestionsCache();
}

export function getLastLocationSuggestionFromLocalStorage(): LocationSuggestion {
  const localStorageValue = localStorage.getItem(LOCAL_STORAGE_KEY_LAST_SEARCHED_LOCATION);

  const validate = locationSuggestionSchema.safeParse(JSON.parse(localStorageValue ?? '{}'));

  if (validate.success === false) {
    return;
  }

  return validate.data;
}

export function clearLastLocationSuggestionFromLocalStorage() {
  localStorage.removeItem(LOCAL_STORAGE_KEY_LAST_SEARCHED_LOCATION);
  invalidateLocationSuggestionsCache();
}

/**
 * Set last searched location cookie for logged out users
 * logged in users will use last searched location from backend to avoid sync issues.
 * cookie should be cleared when a user logs in.
 */
export function setLastLocationAnonymousCookie(suggestion: LocationSuggestion) {
  if (!suggestion || suggestion.type === 'geolocation' || !suggestion.label) return;

  const value: AnonymousLocationCookie = {
    label: suggestion.label,
    ...(typeof suggestion.id === 'number' ? { id: suggestion.id } : {}),
  };

  setCookie(LAST_LOCATION_ANONYMOUS_COOKIE, value, {
    path: '/',
    maxAge: LAST_LOCATION_ANONYMOUS_COOKIE_MAX_AGE,
  });
  invalidateLocationSuggestionsCache();
}

export function persistResolvedLocationForAnonymous(resolvedLocation: ResolvedLocation): void {
  if (loggedUser()) return;

  // resolvedLocation.id is a string (matching the autocomplete contract); the cookie/suggestion
  // model stores it as a number, so convert at the boundary and drop non-numeric/invalid ids.
  const id = Number(resolvedLocation.id);

  setLastLocationAnonymousCookie({
    type: 'history',
    label: resolvedLocation.label,
    ...(Number.isFinite(id) ? { id } : {}),
  });
}

export function clearLastLocationAnonymousCookie() {
  removeCookie(LAST_LOCATION_ANONYMOUS_COOKIE, { path: '/' });
  invalidateLocationSuggestionsCache();
}

export function getLastLocationAnonymousCookie(): AnonymousLocationCookie | undefined {
  const raw = getCookie(LAST_LOCATION_ANONYMOUS_COOKIE);
  if (!raw) return undefined;

  // universal-cookie auto-parses JSON values; tolerate string in case it wasn't.
  const candidate = typeof raw === 'string' ? safeParseJson(raw) : raw;
  const validate = anonymousLocationCookieSchema.safeParse(candidate);
  return validate.success ? validate.data : undefined;
}

function safeParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

async function getLocationSuggestionFromSearchHistory(): Promise<LocationSuggestion> {
  const { data, error } = await promiseWrapper(autocompleteService.getLocationSuggestions(''));

  if (error) {
    console.error('Error while fetching location suggestions: ', error);
    return;
  }

  const [lastLocationSearch] = data?.history;

  setLastLocationSuggestionToLocalStorage(lastLocationSearch);

  return lastLocationSearch;
}

export async function initiLocationValueFromHistory(): Promise<LocationSuggestion | undefined> {
  return resolveDefaultLocation({
    anonymousCookie: getLastLocationAnonymousCookie(),
    cachedLocalStorageSuggestion: getLastLocationSuggestionFromLocalStorage(),
    isLoggedIn: !!loggedUser(),
    fetchUserLastLocation: getLocationSuggestionFromSearchHistory,
  });
}
